import type { Area } from "./permissions";

// Which area guards which admin route. Mirrors the backend's table so the UI
// can tell, before sending, that a write would come back 403.
//
// Order matters: the per-user and per-club sub-resources belong to a different
// area than their parent, so they have to be matched first.
const ROUTE_AREAS: { pattern: RegExp; area: Area }[] = [
  // Sub-resources that override their parent prefix.
  { pattern: /^\/v1\/admin\/users\/[^/]+\/counters/, area: "admin/counters" },
  { pattern: /^\/v1\/admin\/users\/[^/]+\/credits/, area: "admin/credits" },
  { pattern: /^\/v1\/admin\/users\/enforcements/, area: "admin/safety" },
  { pattern: /^\/v1\/admin\/users\/[^/]+\/enforcements/, area: "admin/safety" },
  { pattern: /^\/v1\/admin\/clubs\/[^/]+\/credits/, area: "admin/credits" },
  { pattern: /^\/v1\/admin\/permissions\/template/, area: "admin/special-users" },

  { pattern: /^\/v1\/admin\/users/, area: "admin/users" },
  { pattern: /^\/v1\/admin\/notifications\/location/, area: "admin/users" },
  { pattern: /^\/v1\/admin\/counters/, area: "admin/counters" },
  { pattern: /^\/v1\/admin\/counter-names/, area: "admin/counters" },
  { pattern: /^\/v1\/admin\/credits/, area: "admin/credits" },
  { pattern: /^\/v1\/admin\/cards/, area: "admin/cards" },
  { pattern: /^\/v1\/admin\/badges/, area: "admin/badges" },
  { pattern: /^\/v1\/admin\/clubs/, area: "admin/clubs" },
  { pattern: /^\/v1\/admin\/events/, area: "admin/events" },
  { pattern: /^\/v1\/admin\/configs/, area: "admin/configs" },
  { pattern: /^\/v1\/admin\/reports/, area: "admin/safety" },
  { pattern: /^\/v1\/admin\/enforcements/, area: "admin/safety" },
  { pattern: /^\/v1\/admin\/waitlist/, area: "admin/waitlist" },
  { pattern: /^\/v1\/admin\/special-users/, area: "admin/special-users" },
  { pattern: /^\/v1\/admin\/costs/, area: "admin/costs" },
  { pattern: /^\/v1\/admin\/announcements/, area: "admin/announcements" },
  { pattern: /^\/v1\/admin\/storage/, area: "admin/storage" },
  { pattern: /^\/v1\/admin\/sharding/, area: "admin/sharding" },
];

// The public flag check stays open to everyone.
const FEATURES_CHECK = /^\/v1\/features\/check\//;
const FEATURES = /^\/v1\/features/;

/** The area guarding a path, or null when the route isn't permission-gated. */
export function areaForPath(path: string): Area | null {
  const clean = path.split("?")[0] ?? path;
  if (FEATURES_CHECK.test(clean)) return null;
  if (FEATURES.test(clean)) return "admin/features";
  return ROUTE_AREAS.find(({ pattern }) => pattern.test(clean))?.area ?? null;
}

const WRITE_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);

/** The backend rule: GET needs read, everything else needs write. */
export const isWriteMethod = (method = "GET") =>
  WRITE_METHODS.has(method.toUpperCase());
